import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import Select from 'react-select';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaFilePdf, FaPrint } from 'react-icons/fa';
import excelIcon from '../../assets/icons/excel.png';
import PrintableReceipt from '../../printreceipt';
import ReactDOMServer from 'react-dom/server';
import AppDataGrid from '../../components/common/AppDataGrid';
import { Tooltip as MuiTooltip } from '@mui/material';

const FeeReceiptPrintReport = () => {
    const userData = sessionStorage.getItem('user');
    const userObj = JSON.parse(userData);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [students, setStudents] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [printRowData, setPrintRowData] = useState(null);
    const printComponentRef = useRef();

    const [summary, setSummary] = useState({
        total_receipts: 0,
        cash_total: 0,
        upi_total: 0,
        cheque_total: 0,
        other_total: 0,
    });

    const [academicYears, setAcademicYears] = useState([]);

    const [form, setForm] = useState({
        from_date: '',
        to_date: '',
        class_id: '',
        section_id: '',
        student_name: '',
        fee_receipt_no: '',
        payment_mode: '',
        academic_year_id: userObj?.academic_year_id || '',
    });

    useEffect(() => {
        fetchClasses();
        fetchAcademicYears();
    }, []);

    useEffect(() => {
        fetchStudents();
    }, [form.academic_year_id]);

    useEffect(() => {
        if (form.class_id) fetchSections(form.class_id);
    }, [form.class_id, form.academic_year_id]);

    const fetchClasses = async () => {
        try {
            const res = await axios.post(baseUrl + '/classes/', {
                action: 'READ',
                school_id: userObj.school_id,
            });
            setClasses(res.data || []);
        } catch (err) {
            console.error('Error fetching classes:', err);
        }
    };

    const fetchAcademicYears = async () => {
        try {
            const res = await axios.post(baseUrl + '/AcademicYear/', {
                action: 'READ',
                school_id: userObj.school_id,
            });
            setAcademicYears(res.data || []);
        } catch (err) {
            console.error('Error fetching academic years:', err);
        }
    };

    const fetchSections = async (class_id) => {
        try {
            const res = await axios.post(baseUrl + '/Sections/', {
                action: 'DROPDOWNREAD',
                class_id,
                school_id: userObj.school_id,
                academic_year_id: form.academic_year_id || userObj.academic_year_id,
            });
            setSections(res.data || []);
        } catch (err) {
            console.error('Error fetching sections:', err);
        }
    };

    const fetchStudents = async () => {
        try {
            const res = await axios.post(baseUrl + '/students/', {
                action: 'FILTER',
                school_id: userObj.school_id,
                academic_year_id: form.academic_year_id || userObj.academic_year_id,
            });
            setStudents(res.data || []);
        } catch (err) {
            console.error('Error fetching students:', err);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm((prev) => ({ ...prev, [id]: value ?? '' }));
    };

    const handleStudentChange = (selectedOption) => {
        setForm((prev) => ({ ...prev, student_name: selectedOption?.value || '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            school_id: userObj.school_id,
            from_date: form.from_date || null,
            to_date: form.to_date || null,
            class_id: form.class_id ? parseInt(form.class_id) : null,
            section_id: form.section_id ? parseInt(form.section_id) : null,
            student_name: form.student_name || null,
            receipt_no: form.fee_receipt_no || null,
            payment_mode: form.payment_mode || null,
            academic_year_id: form.academic_year_id ? parseInt(form.academic_year_id) : null,
        };

        try {
            const res = await axios.post(baseUrl + '/dailyfeereceipts_print/', payload);
            const data = res.data || [];

            const nextSummary = {
                total_receipts: data.length,
                cash_total: 0,
                upi_total: 0,
                cheque_total: 0,
                other_total: 0,
            };

            data.forEach((row) => {
                const amount = parseFloat(row.amount_paid || 0);
                const mode = (row.payment_mode || '').toLowerCase();
                if (mode === 'cash') nextSummary.cash_total += amount;
                else if (mode === 'upi') nextSummary.upi_total += amount;
                else if (mode === 'cheque') nextSummary.cheque_total += amount;
                else nextSummary.other_total += amount;
            });

            setReportData(data); // empty array means DataGrid shows "no rows" overlay
            setSummary(nextSummary);
        } catch (err) {
            if (err.response && err.response.status === 404) {
                toast.info('No receipt records found', { autoClose: 3000 });
                setReportData([]);
                setSummary({
                    total_receipts: 0,
                    cash_total: 0,
                    upi_total: 0,
                    cheque_total: 0,
                    other_total: 0,
                });
            } else {
                toast.error('Something went wrong!');
                console.error('Fee Report Submission Error:', err);
            }
        }
    };

    const exportToExcel = () => {
        if (reportData.length === 0) {
            toast.error('No data available to export.');
            return;
        }

        const formatted = reportData.map((row) => ({
            'Receipt Date': row.receipt_date,
            Name: row.student_name,
            'Receipt No': row.fee_receipt_no,
            'Payment Mode': row.payment_mode,
            Class: row.class_name,
            Section: row.section_name,
            'Amount Paid': row.amount_paid,
        }));

        const ws = XLSX.utils.json_to_sheet(formatted);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'FeeReport');
        XLSX.writeFile(wb, 'Fee_Report.xlsx');
    };

    const handlePrintRow = (row) => {
        const printableForm = {
            reciept_date: row.receipt_date,
            admission_number: row.admission_number || '',
            receipt_no: row.fee_receipt_no,
            student_name: row.student_name,
            section_name: row.class_name + ' - ' + row.section_name,
            father_name: row.father_name || '',
            contact_no: row.father_phone_number || '',
            mode: row.payment_mode,
            remarks: row.remarks || '',
        };

        let amountDetails = [];
        if (Array.isArray(row.fee_particulars)) {
            amountDetails = row.fee_particulars;
        } else if (row.fees_item) {
            amountDetails = [{ fees_item: row.fees_item, amount: row.amount_paid }];
        }

        const receiptData = { amountDetails, grandTotal: row.amount_paid };

        setPrintRowData({ form: printableForm, receiptData });
    };

    useEffect(() => {
        if (printRowData) handlePrint();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printRowData]);

    const handlePrint = () => {
        if (!printRowData) {
            toast.error('Nothing to print');
            return;
        }

        const printableHTML = ReactDOMServer.renderToString(
            <PrintableReceipt form={printRowData.form} receiptData={printRowData.receiptData} />
        );

        const printWindow = window.open('', '_blank', 'width=900,height=700');

        if (printWindow) {
            printWindow.document.write(`${printableHTML}`);
            printWindow.focus();
            printWindow.print();
            printWindow.document.close();
            printWindow.close();
        } else {
            alert('Unable to open print window. Please check your popup blocker.');
        }
    };

    const exportToPDF = () => {
        if (reportData.length === 0) {
            toast.error('No data available to export.');
            return;
        }

        const doc = new jsPDF();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(14);
        doc.text('Fee Report', 14, 15);

        const columns = ['Receipt Date', 'Name', 'Receipt No', 'Payment Mode', 'Class', 'Section', 'Amount Paid'];
        const rows = reportData.map((row) => [
            row.receipt_date || '',
            row.student_name || '',
            row.fee_receipt_no || '',
            row.payment_mode || '',
            row.class_name || '',
            row.section_name || '',
            row.amount_paid || '',
        ]);

        autoTable(doc, {
            startY: 20,
            head: [columns],
            body: rows,
            styles: { fontSize: 10 },
        });

        const finalY = doc.lastAutoTable.finalY || 30;

        const cash = `${summary.cash_total.toFixed(2)}`;
        const upi = `${summary.upi_total.toFixed(2)}`;
        const cheque = `${summary.cheque_total.toFixed(2)}`;
        const other = `${summary.other_total.toFixed(2)}`;
        const total = (summary.cash_total + summary.upi_total + summary.cheque_total + summary.other_total).toFixed(2);

        doc.setFontSize(12);
        doc.text('Collection Summary:', 14, finalY + 10);
        doc.setFontSize(11);
        doc.text('Cash Collected: ' + cash, 14, finalY + 20);
        doc.text('UPI Collected: ' + upi, 14, finalY + 28);
        doc.text('Cheque Collected: ' + cheque, 14, finalY + 36);
        if (summary.other_total > 0) {
            doc.text('Other Collected: ' + other, 14, finalY + 44);
            doc.setFontSize(12);
            doc.text('Total Collected: ' + total, 14, finalY + 54);
        } else {
            doc.setFontSize(12);
            doc.text('Total Collected: ' + total, 14, finalY + 44);
        }

        doc.save('Fee_Report.pdf');
    };

    // ===== MUI DataGrid columns (centered & Tooltip), with conditional Action column =====
    const buildColumns = () => {
        const base = [
            {
                field: 'receipt_date',
                headerName: 'Receipt Date',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span>{params.value ?? ''}</span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'student_name',
                headerName: 'Student Name',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {params.value ?? ''}
                        </span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'father_name',
                headerName: 'Father Name',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {params.value ?? ''}
                        </span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'father_phone_number',
                headerName: 'Phone Number',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span>{params.value ?? ''}</span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'fees_item',
                headerName: 'Fee Item',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {params.value ?? ''}
                        </span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'fee_receipt_no',
                headerName: 'Receipt No',
                flex: 1,
                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {params.value ?? ''}
                        </span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'payment_mode',
                headerName: 'Payment Mode',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span>{params.value ?? ''}</span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'class_name',
                headerName: 'Class',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span>{params.value ?? ''}</span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'section_name',
                headerName: 'Section',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={params.value ?? ''}>
                        <span>{params.value ?? ''}</span>
                    </MuiTooltip>
                ),
            },
            {
                field: 'amount_paid',
                headerName: 'Amount Paid',
                flex: 1,

                headerAlign: 'center',
                align: 'center',
                sortable: true,
                renderCell: (params) => (
                    <MuiTooltip title={String(params.value ?? '')}>
                        <span>{params.value ?? ''}</span>
                    </MuiTooltip>
                ),
            },
        ];

        // Conditionally add Action (Print) column if there are real rows
        if (reportData.length > 0) {
            base.push({
                field: 'actions',
                headerName: 'Action',
                flex: 1,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                renderCell: (params) => (
                    <Button variant="outline-secondary" size="sm" onClick={() => handlePrintRow(params.row)}>
                        <FaPrint />
                    </Button>
                ),
            });
        }

        return base;
    };

    // Unique row id
    const getRowId = (row) =>
        row.id ??
        row.fee_receipt_id ??
        `${row.fee_receipt_no || ''}-${row.admission_number || ''}-${row.receipt_date || ''}`;

    return (
        <div className="pageBody">
            <ToastContainer />
            <Container fluid>
                <Card>
                    <Card.Body className="hide-scrollbar">
                        <form onSubmit={handleSubmit}>
                            <Row>
                                <Col xs={12}>
                                    <h6 className="commonSectionTitle">Fee Report</h6>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>Academic Year</Form.Label>
                                            <Form.Select
                                                id="academic_year_id"
                                                value={form.academic_year_id}
                                                onChange={handleInputChange}
                                            >
                                                <option value="" disabled hidden>Select Academic Year</option>
                                                {academicYears.map((aca) => (
                                                    <option key={aca.academic_year_id} value={aca.academic_year_id}>
                                                        {aca.academic_year_name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>From Date</Form.Label>
                                            <Form.Control type="date" id="from_date" value={form.from_date} onChange={handleInputChange} />
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>To Date</Form.Label>
                                            <Form.Control type="date" id="to_date" value={form.to_date} onChange={handleInputChange} />
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>Class</Form.Label>
                                            <Form.Select id="class_id" value={form.class_id} onChange={handleInputChange}>
                                                <option value=""  disabled hidden>Select Class</option>
                                                {classes.map((cls) => (
                                                    <option key={cls.class_id} value={cls.class_id}>
                                                        {cls.class_name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>Section</Form.Label>
                                            <Form.Select id="section_id" value={form.section_id} onChange={handleInputChange}>
                                                <option value=""  disabled hidden>Select Section</option>
                                                {sections.map((sec) => (
                                                    <option key={sec.section_id} value={sec.section_id}>
                                                        {sec.section_name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>Student</Form.Label>
                                            <Select
                                                id="student_name"
                                                options={students.map((s) => ({
                                                    value: `${s.student_first_name} ${s.student_last_name}`,
                                                    label: `${s.student_first_name} ${s.student_last_name}`,
                                                }))}
                                                value={form.student_name ? { value: form.student_name, label: form.student_name } : null}
                                                onChange={handleStudentChange}
                                                isClearable
                                                placeholder="Select Student"
                                                styles={{
                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                                menuPortalTarget={document.body}
                                            />
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>Receipt No</Form.Label>
                                            <Form.Control
                                                id="fee_receipt_no"
                                                value={form.fee_receipt_no}
                                                onChange={handleInputChange}
                                                placeholder="Enter Receipt No"
                                            />
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} xxl={3}>
                                    <div className="commonInput">
                                        <Form.Group>
                                            <Form.Label>Payment Mode</Form.Label>
                                            <Form.Select id="payment_mode" value={form.payment_mode} onChange={handleInputChange}>
                                                <option value=""  disabled hidden>Select Mode Of Payment</option>
                                                <option value="UPI">UPI</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Cheque">Cheque</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </Col>
                                <Col>
                                    <div className="d-flex justify-content-end mt-4">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            className="btn-info clearBtn me-3"
                                            onClick={() =>
                                                setForm({
                                                    from_date: '',
                                                    to_date: '',
                                                    class_id: '',
                                                    section_id: '',
                                                    student_name: '',
                                                    fee_receipt_no: '',
                                                    payment_mode: '',
                                                    academic_year_id: userObj?.academic_year_id || '',
                                                })
                                            }
                                        >
                                            Reset
                                        </Button>
                                        <Button type="submit" variant="primary" className="btn-success primaryBtn">
                                            Search
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </Card.Body>
                </Card>

                <Row className="mt-3 mb-2 align-items-center">
                    <Col>
                        <span style={{ fontSize: '15px' }}>Total Receipts: {summary.total_receipts}</span>
                    </Col>
                    <Col>
                        <span style={{ fontSize: '15px' }}>Cash: ₹{summary.cash_total.toFixed(2)}</span>
                    </Col>
                    <Col>
                        <span style={{ fontSize: '15px' }}>UPI: ₹{summary.upi_total.toFixed(2)}</span>
                    </Col>
                    <Col>
                        <span style={{ fontSize: '15px' }}>Cheque: ₹{summary.cheque_total.toFixed(2)}</span>
                    </Col>
                    {summary.other_total > 0 && (
                        <Col>
                            <span style={{ fontSize: '15px' }}>Other: ₹{summary.other_total.toFixed(2)}</span>
                        </Col>
                    )}
                    <Col className="d-flex justify-content-end">
                        <Button variant="success" onClick={exportToExcel} className="me-2">
                            <img src={excelIcon} alt="excel" style={{ width: 20, marginRight: 5 }} />
                        </Button>
                        <Button variant="danger" onClick={exportToPDF}>
                            <FaFilePdf style={{ marginRight: 5 }} />
                        </Button>
                    </Col>
                </Row>

                <div className="commonTable height100 mt-2">
                    <div className="tableBody">
                        <AppDataGrid
                            rows={reportData.length ? reportData : []}
                            columns={buildColumns()}
                            getRowId={getRowId}
                           pageSizeOptions={[10, 20, 50]} 
                            showToolbar={false}
                            pagination
                            density="compact"
                        />

                        {printRowData && (
                            <div style={{ display: 'none' }}>
                                <div ref={printComponentRef}>
                                    <PrintableReceipt form={printRowData.form} receiptData={printRowData.receiptData} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default FeeReceiptPrintReport;
